import React, { useState } from 'react'
import classNames from 'classnames'
import { motion } from 'framer-motion'
import { MoreHorizontal, Trash2, Download } from 'lucide-react'
import { downloadFile } from '../../../../utils/download'

export interface VideoData {
  jobId: number
  resultUrl: string
  requestType: 'video'
  userPrompt: string
  aspectRatio: string
  resolution: string
  durationSeconds: number
  status: 'pending' | 'processing' | 'completed' | 'failed'
  createdAt: string
  completedAt: string
  requestId: number
  pointCost: number
}

interface VideoItemProps {
  video: VideoData
  isDarkMode: boolean
  onDelete?: (id: string) => void
  onSettingsClick?: (id: number) => void
}

export const VideoItem: React.FC<VideoItemProps> = ({
  video,
  isDarkMode,
  onDelete,
  onSettingsClick,
}) => {
  const [showSettings, setShowSettings] = useState(false)

  // 새로운 API에서는 job 단위로 직접 데이터가 옴
  const isCompleted = video.status === 'completed' && video.resultUrl

  const handleSettingsClick = () => {
    setShowSettings(!showSettings)
    if (onSettingsClick) {
      onSettingsClick(video.jobId)
    }
  }

  const handleDeleteClick = () => {
    if (window.confirm('이 비디오를 삭제하시겠습니까?')) {
      if (onDelete) {
        onDelete(video.jobId.toString())
      }
    }
  }

  const handleDownload = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (video.resultUrl) {
      const filename = `veo-video-${video.jobId}.mp4`
      await downloadFile(video.resultUrl, filename, {
        title: 'VEO 생성 비디오',
        text: 'VEO로 생성한 비디오를 저장하거나 공유하세요',
      })
    }
  }

  // Pending/Processing states
  if (video.status === 'pending' || video.status === 'processing') {
    return (
      <motion.div
        className={classNames('rounded-xl border p-4 transition-all', {
          'border-zinc-700 bg-zinc-800': isDarkMode,
          'border-gray-200 bg-white': !isDarkMode,
        })}
      >
        <div className="flex flex-row gap-4">
          {/* 5/4 영역: 스켈레톤 */}
          <div className="relative flex-[4]">
            <div className="aspect-video overflow-hidden rounded-lg bg-gradient-to-br from-sky-200 via-indigo-300 to-purple-400">
              <div className="h-full w-full animate-skeleton bg-gradient-to-r from-transparent via-white/40 to-transparent"></div>
              <div className="absolute inset-0 bg-white opacity-10 mix-blend-multiply"></div>
              <div
                className="absolute inset-0"
                style={{
                  backgroundImage: `
                  repeating-linear-gradient(45deg, 
                    transparent, 
                    transparent 8px, 
                    rgba(255,255,255,0.1) 8px, 
                    rgba(255,255,255,0.1) 12px
                  ),
                  repeating-linear-gradient(-45deg, 
                    transparent, 
                    transparent 6px, 
                    rgba(255,255,255,0.08) 6px, 
                    rgba(255,255,255,0.08) 10px
                  )
                `,
                }}
              ></div>
              {/* 생성중 태그 */}
              <div className="absolute left-2 top-2 z-10 rounded bg-[rgba(39,39,39,0.7)] px-2 py-1">
                <span className="text-xs font-bold text-white">
                  {video.status === 'processing' ? '생성중' : '대기중'}
                </span>
              </div>
            </div>
          </div>

          {/* 5/1 영역: 프롬프트 정보 */}
          <div className="flex flex-[1] flex-col justify-center">
            <div
              className={classNames('text-sm', {
                'text-gray-300': isDarkMode,
                'text-gray-700': !isDarkMode,
              })}
            >
              <div className="truncate">{video.userPrompt}</div>
              <div
                className={classNames('mt-1 text-xs', {
                  'text-gray-400': isDarkMode,
                  'text-gray-500': !isDarkMode,
                })}
              >
                {video.resolution} • {video.durationSeconds}s •{' '}
                {video.aspectRatio}
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    )
  }

  // Failed state
  if (video.status === 'failed') {
    return (
      <motion.div
        className={classNames('rounded-xl border p-4 transition-all', {
          'border-zinc-700 bg-zinc-800': isDarkMode,
          'border-gray-200 bg-white': !isDarkMode,
        })}
      >
        <div className="flex flex-row gap-4">
          {/* 5/4 영역: 실패 화면 */}
          <div className="relative flex-[4]">
            <div className="flex aspect-video items-center justify-center overflow-hidden rounded-lg bg-gradient-to-br from-red-300 to-red-500">
              <div className="text-center text-white">
                <div className="mb-1 text-sm font-bold">생성 실패</div>
                <div className="text-xs opacity-80">다시 시도해주세요</div>
              </div>
              {/* 실패 태그 */}
              <div className="absolute left-2 top-2 z-10 rounded bg-[rgba(220,38,38,0.8)] px-2 py-1">
                <span className="text-xs font-bold text-white">실패</span>
              </div>
            </div>
          </div>

          {/* 5/1 영역: 프롬프트 정보 */}
          <div className="flex flex-[1] flex-col justify-center">
            <div
              className={classNames('text-sm', {
                'text-gray-300': isDarkMode,
                'text-gray-700': !isDarkMode,
              })}
            >
              <div className="truncate">{video.userPrompt}</div>
              <div
                className={classNames('mt-1 text-xs', {
                  'text-gray-400': isDarkMode,
                  'text-gray-500': !isDarkMode,
                })}
              >
                {video.resolution} • {video.durationSeconds}s •{' '}
                {video.aspectRatio}
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    )
  }

  // Completed videos
  return (
    <motion.div
      className={classNames(
        'group rounded-xl border p-4 transition-all hover:shadow-lg',
        {
          'hover:bg-zinc-750 border-zinc-700 bg-zinc-800': isDarkMode,
          'border-gray-200 bg-white hover:bg-gray-50': !isDarkMode,
        },
      )}
    >
      {/* 5/4 + 5/1 flex-row layout */}
      <div className="flex flex-row gap-4">
        {/* 5/4 영역: 비디오 화면 */}
        <div className="relative flex-[4]">
          <div className="aspect-video overflow-hidden rounded-lg">
            <video
              src={video.resultUrl}
              className="h-full w-full object-contain"
              controls
              muted
              preload="metadata"
            />
          </div>

          {/* 다운로드 버튼 - 호버시에만 표시 */}
          {video.resultUrl && (
            <div className="absolute right-3 top-3 opacity-0 transition-opacity group-hover:opacity-100">
              <button
                onClick={handleDownload}
                className="rounded-md border border-[rgba(255,255,255,0.2)] bg-[rgba(55,59,66,0.7)] p-3 transition-colors hover:bg-[rgba(55,59,66,0.9)]"
                title="비디오 다운로드"
              >
                <Download size={16} className="text-white" />
              </button>
            </div>
          )}
        </div>

        {/* 5/1 영역: 버튼들을 아래쪽에 배치 */}
        <div className="relative flex flex-[1] flex-col justify-end">
          <div className="flex select-none flex-row gap-0.5 lg:gap-2">
            {/* Settings 버튼 */}
            <button
              type="button"
              onClick={handleSettingsClick}
              className={classNames(
                'inline-flex h-6 items-center justify-center gap-1 rounded-lg border px-2 text-xs font-medium transition',
                {
                  'border-gray-300 bg-white text-gray-700 hover:bg-gray-50':
                    !isDarkMode,
                  'border-zinc-700 bg-zinc-800 text-gray-300 hover:bg-zinc-700':
                    isDarkMode,
                  'ring-2 ring-blue-500': showSettings,
                },
              )}
            >
              <MoreHorizontal className="h-3.5 w-3.5" />
              Settings
            </button>

            {/* Delete 버튼 - 아이콘만 */}
            <button
              type="button"
              onClick={handleDeleteClick}
              className={classNames(
                'inline-flex h-6 w-6 items-center justify-center rounded-lg border text-xs font-medium transition',
                {
                  'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:text-red-500':
                    !isDarkMode,
                  'border-zinc-700 bg-zinc-800 text-gray-300 hover:bg-zinc-700 hover:text-red-500':
                    isDarkMode,
                },
              )}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>

          {/* Settings 팝업 */}
          {showSettings && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 10 }}
              className={classNames(
                'absolute bottom-8 right-0 z-10 min-w-48 rounded-lg border p-3 shadow-lg',
                {
                  'border-zinc-700 bg-zinc-800 text-white': isDarkMode,
                  'border-gray-200 bg-white text-gray-900': !isDarkMode,
                },
              )}
            >
              <div className="space-y-2">
                <div className="mb-3 text-xs font-medium opacity-70">
                  Video Settings
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-xs opacity-70">Quality:</span>
                  <span className="text-xs font-medium">
                    {video.resolution}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-xs opacity-70">Duration:</span>
                  <span className="text-xs font-medium">
                    {video.durationSeconds}s
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-xs opacity-70">Ratio:</span>
                  <span className="text-xs font-medium">
                    {video.aspectRatio}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-xs opacity-70">Created:</span>
                  <span className="text-xs font-medium">
                    {new Date(video.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </div>

              {/* 닫기 버튼 */}
              <button
                onClick={() => setShowSettings(false)}
                className={classNames(
                  'absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full text-xs transition-transform',
                  {
                    'bg-zinc-700 text-white hover:bg-zinc-600': isDarkMode,
                    'bg-gray-100 text-gray-600 hover:bg-gray-200': !isDarkMode,
                  },
                )}
              >
                ×
              </button>
            </motion.div>
          )}
        </div>
      </div>
    </motion.div>
  )
}
